import { useQueries, useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import { PageHeader, WidgetCard } from '@/components/admin/AdminShell';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/admin/states';
import {
  FIELD_LABELS,
  FIELDS_BY_TYPE,
  fetchPresidentResponse,
  fetchPresidentResponses,
  type PresidentResponseDetail,
} from '@/features/presidentSurveys/api';

/** 固定フィールドを「項目名 → 値」の組で並べる（空の項目は出さない / D-146） */
function FieldList({ detail }: { detail: PresidentResponseDetail }): ReactElement {
  const fields = FIELDS_BY_TYPE[detail.survey_type]
    .map((key) => ({ key, label: FIELD_LABELS[key], value: detail[key] }))
    .filter((f) => f.value !== null && f.value !== '');
  if (fields.length === 0) {
    return <p className="text-wptext-muted">開示された内容はありません。</p>;
  }
  return (
    <dl className="flex flex-col gap-2">
      {fields.map((field) => (
        <div key={field.key}>
          <dt className="font-semibold text-wptext-strong">{field.label}</dt>
          <dd className="whitespace-pre-wrap">
            {field.key === 'satisfaction_score' ? `${field.value} / 5` : String(field.value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * 受講生「社長からの期待」（implementation-spec §6）。
 * 入口の期待（As is / To be）と最新の評価を左右に並べて対比でき、その下に成長評価を
 * 時系列で並べる。開示可（member_and_director）の回答セットだけがAPIから返る（D-146）。
 */
export function PresidentExpectationsPage(): ReactElement {
  const listQuery = useQuery({
    queryKey: ['president-responses', 'my'],
    queryFn: () => fetchPresidentResponses(),
  });

  const detailQueries = useQueries({
    queries: (listQuery.data ?? []).map((summary) => ({
      queryKey: ['president-response', summary.id],
      queryFn: () => fetchPresidentResponse(summary.id),
    })),
  });

  if (listQuery.isPending) return <SkeletonRows rows={4} />;
  if (listQuery.isError) {
    return (
      <ErrorState
        message="社長からの期待の取得に失敗しました。"
        onRetry={() => void listQuery.refetch()}
      />
    );
  }

  const details = detailQueries
    .map((q) => q.data)
    .filter((d): d is PresidentResponseDetail => d !== undefined);
  const sortKey = (d: PresidentResponseDetail): string => d.answered_at ?? d.created_at;
  const startResponses = details
    .filter((d) => d.survey_type === 'start_expectation')
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
  const growthResponses = details
    .filter((d) => d.survey_type === 'growth_evaluation')
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

  const firstStart = startResponses.length > 0 ? startResponses[0] : null;
  const latestGrowth =
    growthResponses.length > 0 ? growthResponses[growthResponses.length - 1] : null;

  return (
    <>
      <PageHeader
        title="社長からの期待"
        description="派遣元企業の社長から共有された内容のうち、開示されている項目を表示します。"
      />

      {details.length === 0 && (
        <EmptyState
          title="開示された内容はまだありません"
          description="社長アンケートが登録・開示されるとここに表示されます。"
        />
      )}

      {/* 入口の期待と最新の評価を左右に並べて対比できるようにする（implementation-spec §6 / D-066） */}
      {firstStart && latestGrowth && (
        <WidgetCard title="入口の期待と最新の評価">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h3 className="mb-2 border-b border-wpborder pb-1 font-semibold text-wptext-strong">
                参加開始時の期待（{firstStart.survey_round}）
              </h3>
              <FieldList detail={firstStart} />
            </div>
            <div>
              <h3 className="mb-2 border-b border-wpborder pb-1 font-semibold text-wptext-strong">
                最新の評価（{latestGrowth.survey_round}）
              </h3>
              <FieldList detail={latestGrowth} />
            </div>
          </div>
        </WidgetCard>
      )}

      {startResponses.map((response) => (
        <WidgetCard key={response.id} title={`参加開始時の期待 — ${response.survey_round}`}>
          <FieldList detail={response} />
          {response.answered_at && (
            <p className="mt-2 text-wptext-muted">
              聴取日: {new Date(response.answered_at).toLocaleDateString('ja-JP')}
            </p>
          )}
        </WidgetCard>
      ))}

      {growthResponses.length > 0 && (
        <>
          <h2 className="mb-2 mt-6 text-lg font-semibold text-wptext-strong">成長評価の推移</h2>
          <div className="flex flex-col gap-3">
            {growthResponses.map((response) => (
              <WidgetCard key={response.id} title={`成長評価 — ${response.survey_round}`}>
                <FieldList detail={response} />
              </WidgetCard>
            ))}
          </div>
        </>
      )}
    </>
  );
}
